/** Pinia store module for a user of Square Bell Data API
 * 
 * Representation of user's login status and details
 */

import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'

import { useSbappStore } from '@/store/sbapp-store.mjs'
import sbdata from './sbdata-config.yaml'

import { debug, processPendingEvents } from '@/lib/sb_utility.mjs'

export function validEmailCode(u_code) {
   const codeRegex = /^[a-zA-Z0-9_\-]{20}$/
   if (typeof u_code != 'string') return false
   else if (codeRegex.test(u_code)) return true
   else return false
}
export function validOrgId(u_org) {
   const orgRegex = /^[1-9][0-9]{2,14}$/
   if (orgRegex.test(String(u_org))) return true
   else return false
}

export const useSbuserStore = defineStore('sbuser', () => {
   debug('SBUser store setup', "sbuser")
   const sbapp = useSbappStore()

   // Lookup appropriate data server for current host
   const myHostname = location.hostname
   const dataUrl = sbdata.url[myHostname]
   console.log(`Dataurl: ${dataUrl}`)

   const name = ref("")
   // loginStatus <0 if error, >0 if logged in
   const loginStatus = ref(0), loginStatusMsg = ref("")
   const org = reactive({
      id: sbapp.recall('OrgId') ?? 0,
      nickname: "",
   })
   const authToken = ref(false), webcode = ref(false)
   const dataHolders = reactive([])

   const sbAppChannel = new BroadcastChannel('sbdata-users')
   const otherTabs = ref(0)
   function AppChannelSend(msg, data){
      debug(`Sending '${msg}' to AppChannel`, 'sbuser')
      data.type = msg
      sbAppChannel.postMessage(data)
   }
   function shareWebcode(){
      AppChannelSend('webcode', {
         app: sbapp.idStr,
         webcode: webcode.value,
         otherTabs: otherTabs.value
      })
   }
   sbAppChannel.onmessage = function(message){
      let msg = message.data
      debug('received message: ' + JSON.stringify(msg), "sbuser")
      switch(msg.type){
         case 'join':
            otherTabs.value++
            if(webcode.value) shareWebcode()
            if( otherTabs.value==1) AppChannelSend('rejoin', {app: sbapp.idStr})
            break;
         case 'rejoin':
            otherTabs.value++
            if(webcode.value) shareWebcode()
            break;
         case 'webcode':
            if( !webcode.value ){
               webcode.value = msg.webcode
               otherTabs.value = Math.max(msg.otherTabs, otherTabs.value)
            } else if( webcode.value != msg.webcode ){
               // TODO: Invalidate authToken / mark as logged out
               webcode.value = msg.webcode
               otherTabs.value = Math.max(msg.otherTabs, otherTabs.value)
            }
            break;
      }
   }
   AppChannelSend('join', { app: sbapp.idStr })

   return {
      // State
      name,
      // loginStatus <0 if error, >0 if logged in
      loginStatus, loginStatusMsg,
      org,
      authToken, webcode,
      otherTabs,

      // Getters
      fullName: computed(() => name.value),
      orgName: computed(() => org.nickname),
      isLoggedIn: computed(() => (loginStatus.value > 0)),

      // Actions
      registerDataHolder(listenerFn) {
         // Add a listener to consult at logout/close
         if (!dataHolders.includes(listenerFn))
            dataHolders.push(listenerFn)
      },
      async logout() {
         debug("logout", "sbuser")
         const sbapp = useSbappStore()
         // TODO: Consult dataHolders about logging out
         /*let allSaved = dataHolders.every(
            (listener)=>listener({type: "save"})
         )*/
         // Delete all user data
         let logoutResult = dataHolders.reduce((agg, listener) => {
            return agg + listener({
               type: "logout", force: true
            })
         }, "")
         debug("logout all: " + logoutResult, "sbuser")
         //debug("logout user", "sbuser")
         authToken.value = false
         webcode.value = false
         loginStatus.value = 0
         loginStatusMsg.value = ""
         // Only forget OrgId if not using localStorage
         org.id = sbapp.recall('OrgId') ?? 0
         loginStatusMsg.value = "For best security, please close this browser window."
         this.$router.push("/login")
      },
      async webInit() {
         debug("webInit", "sbuser")
         if(!webcode.value && otherTabs.value==0){
            // Check no other tabs waiting to let us know the webcode
            await processPendingEvents()
            if( webcode.value ) return true
         }

         let response = await fetch(dataUrl + 'initweb', {
            mode: 'cors', credentials: 'include'
         }).catch((err) => ({ ok: false, err }))
         if (!response.ok) return false
         let data = await response.json()
         if (data.hasOwnProperty(sbdata.WebcodeHeaderName)) {
            webcode.value = data[sbdata.WebcodeHeaderName]
            if(otherTabs.value>0) shareWebcode(sbAppChannel)
            return true
         } else {
            webcode.value = false
            return false
         }
      },
      async sbdataPost(endpoint, data) {
         debug("sbdatapost", "sbuser")
         const url = dataUrl + endpoint
         const body = JSON.stringify(data)
         const headers = {
            "Content-Type": "application/json",
            [sbdata.WebcodeHeaderName]: this.webcode,
            "Cache-Control": "no-cache"
         }
         if (authToken.value) headers.authorization = authToken.value
         return fetch(url, {
            method: 'POST',
            headers,
            body,
            mode: 'cors',
            credentials: 'include',
         })
      },
      async sbdataGet(endpoint) {
         debug("sbdataget", "sbuser")
         const url = dataUrl + endpoint
         const headers = {
            //"Content-Type": "application/json",
            [sbdata.WebcodeHeaderName]: webcode.value,
            "Cache-Control": "no-cache"
         }
         if (authToken.value) headers.Authorization = authToken.value
         return fetch(url, {
            method: 'GET',
            headers,
            mode: 'cors',
            credentials: 'include',
         })
      },
      async requestEmailCode(email) {
         debug("requestemailcode", "sbuser")
         if(!webcode.value) {
            let initOk = await this.webInit()
            if (!initOk) return false
         }

         const sbapp = useSbappStore()
         let response = await this.sbdataPost(
            "auth/request-email-code",
            {
               email,
               org: org.id,
               app: sbapp.idStr
            }
         ).catch((err) => ({ ok: false, err }))
         if (response.ok) {
            loginStatusMsg.value = `A login code has been requested.
                If your email (${email}) is registered with 
                organisation ${org.id},
                you should receive a link to log in shortly.`
            return true
         } else return Promise.reject(false)
      },
      async verifyEmailCode(u_code) {
         debug("verifyEmailCode", "sbuser")
         if (!webcode.value) {
            let initOk = await this.webInit()
            if (!initOk) return false
         }
         if (!validEmailCode(u_code)) return Promise.reject("Invalid code")
         let response = await this.sbdataPost(
            "auth/verify-email-code",
            {
               code: u_code,
               org: org.id
            }
         )
         if (response.ok) {
            // TODO: Save token and user details
            let data = await response.json()
            debug(data, "sbuser")
            authToken.value = data.authToken
            name.value = data.userDetails.fullName
            this.setOrgId(data.userDetails.orgId)

            loginStatus.value = 1
            loginStatusMsg.value = "Logged in via email code"
            dataHolders.forEach((listener) => {
               listener({ type: 'login' })
            })
            const sbapp = useSbappStore()
            sbapp.remember('email', data.userDetails.email)
            return data.userDetails
         } else {
            loginStatus.value = -1
            loginStatusMsg.value = "The code could not be verified."
            return Promise.reject("Could not log in")
         }
      },
      setOrgId(u_org) {
         if (validOrgId(u_org)) {
            const sbapp = useSbappStore()
            org.id = Number(u_org)
            sbapp.remember('OrgId', u_org)
         }
      },
   }
})